import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Layout } from 'blaze-validator';
import { Col } from 'react-bootstrap';
import hash from 'object-hash';
import browserHistory from '../../history';
import toastr from '../toastr';
import LoadingPage from '../LoadingPage';
import { hasInput, convertOptions } from './ReportUtils';
import Form from '../form/Form';
import '../../styles/scheduler.scss';
import ApiService from '../../services/ApiService';
import moment from 'moment';

const hourOptions = [{ label: 'Midnight', value: 0 }];

for (let i = 1; i < 12; i++) {
  hourOptions.push({ label: `${i}AM`, value: i });
}

hourOptions.push({ label: 'Noon', value: 12 });

for (let i = 1; i < 12; i++) {
  hourOptions.push({ label: `${i}PM`, value: 12 + i });
}

const frequencyOptions = [
  { label: 'Daily', value: 'D' },
  { label: 'Mondays', value: 'MO' },
  { label: 'Tuesdays', value: 'TU' },
  { label: 'Wednesdays', value: 'WE' },
  { label: 'Thursdays', value: 'TH' },
  { label: 'Fridays', value: 'FR' },
  { label: 'Saturdays', value: 'SA' },
  { label: 'Sundays', value: 'SU' },
  { label: 'Weekdays', value: 'MF' },
  { label: 'Beginning of month', value: 'BOM' },
  { label: 'End of month', value: 'EOM' },
];

if (process.env.NODE_ENV !== 'production') {
  frequencyOptions.unshift({ label: 'Every Minute', value: 'I' });
}

for (let i = 1; i < 32; i++) {
  frequencyOptions.push({ label: `Day ${i} of month`, value: i.toString() });
}

const getOptionsHash = options => {
  if (!options || !options.length) {
    return hash([]);
  }

  return hash(options.filter(o => !o.skiplayouthash).map(o => o.value));
};

const getEmptyLayout = (reportName, reportFields = []) => {
  return [
    {
      type: 'section',
      fields: [
        {
          control: 'paragraph',
          name: 'para',
          style: 'banner center',
          data: `No options required for ${reportName}`,
        },
        ...reportFields,
      ],
    },
  ];
};

const fetchOptions = (reportName, reportOptions) => {
  if (!reportName) {
    return Promise.resolve({ layout: [], options: null, dynamic: false });
  }

  return ApiService.get(`scheduler/page/${reportName}`).then(({ data }) => {
    const optionsJson = reportOptions || [];
    const form = {};
    optionsJson.forEach((d, i) => {
      form[i] = d;
    });

    const { options, dynamic = false } = data;

    const reportFields = new Layout(convertOptions(options)).populateLayout(form).getLayout();

    if (!hasInput(options)) {
      return { options, layout: getEmptyLayout(reportName, reportFields), dynamic }; // eslint-disable-line
    }

    return {
      options,
      dynamic,
      layout: [
        {
          type: 'section',
          label: 'Report Options',
          style: 'green',
          fields: reportFields,
        },
      ],
    };
  });
};

const Scheduler = ({ action, id }) => {
  const [loading, setLoading] = useState(action === 'edit');
  const [key, setKey] = useState(1);
  const [gridLayouts, setGridLayouts] = useState(null);
  const [reportLayout, setReportLayout] = useState([]);
  const [dynamic, setDynamic] = useState(false);
  const [fetchingOptions, setFetchingOptions] = useState(false);

  const formRef = useRef(null);
  const reportFormRef = useRef(null);
  const reportDataRef = useRef(null);
  const reportOptionsRef = useRef(null);

  const user = useSelector(state => state.app.user);
  const hasArchive = useSelector(state => state.app.config.hasArchive);

  const getReportOptionsData = () => {
    return reportFormRef.current ? reportFormRef.current.fec.current.manager.exportDataObject() : null;
  };

  const getGridLayoutOptions = pageName => {
    if (!gridLayouts) {
      return [];
    }

    const reportData = getReportOptionsData();

    let optionsHash = null;
    if (reportOptionsRef.current && reportData) {
      reportOptionsRef.current.forEach((obj, i) => {
        obj.value = String(reportData[i]);
      });

      optionsHash = getOptionsHash(reportOptionsRef.current);
    }

    return gridLayouts
      .filter(gridLayout => {
        if (gridLayout.pageName !== pageName || (dynamic && gridLayout.optionsHash !== optionsHash)) {
          return false;
        }

        return true;
      })
      .map(gridLayout => {
        return { label: gridLayout.name, value: String(gridLayout.id) };
      });
  };

  const updateGridLayoutOptions = pageName => {
    if (!formRef.current || !gridLayouts) {
      return;
    }

    const { updateForm } = formRef.current;

    if (!pageName) {
      updateForm({ layoutId: { options: [] } });
      return;
    }

    updateForm({
      layoutId: {
        options: getGridLayoutOptions(pageName),
      },
    });
  };

  const getLayout = ({ gridLayoutOptions = [] } = {}) => [
    {
      type: 'section',
      struct: true,
      fields: [
        {
          name: 'name',
          label: 'Job Name',
          required: true,
          pattern: /^[a-zA-Z0-9-_\s]+$/,
          col: 6,
        },
        {
          name: 'format',
          label: 'Format',
          control: 'select',
          data: 'csv',
          required: true,
          noEmptyOption: true,
          options: [
            { label: 'CSV', value: 'csv' },
            { label: 'PDF', value: 'pdf' },
          ],
          col: 6,
        },
        {
          name: 'branches',
          label: 'Branch',
          col: {
            sm: 12,
            md: 12,
            lg: 12,
          },
          control: 'tags',
          required: true,
          data: [],
          validation: {
            type: 'db',
            modelName: 'branch',
            allowSelectAll: true,
            filterSuggestions: suggestions =>
              suggestions.filter(s => user.branches.findIndex(u => u.id === s.id) !== -1),
          },
        },
        {
          name: 'pageName',
          label: 'Report',
          col: {
            sm: 12,
            md: 6,
            lg: 6,
          },
          data: '',
          validation: {
            type: 'page',
            filter: data => data.filter(x => x.type === 'viewReport' || x.type === 'viewWebSolutions'),
          },
          required: true,
        },
        {
          name: 'layoutId',
          label: 'Layout',
          col: {
            sm: 12,
            md: 6,
            lg: 6,
            clear: true,
          },
          options: gridLayoutOptions,
        },
        {
          label: 'Report Parameters',
          name: 'params',
          // required: true,
          hidden: true,
        },
        {
          label: 'Has Input',
          name: 'hasInput',
          hidden: true,
        },
        {
          name: 'email',
          control: 'Tags',
          type: 'email',
          label: 'Emails',
          data: [],
        },
        {
          name: 'emailAttachments',
          label: 'Attach Files To Email',
          control: 'checkbox',
          type: 'boolean',
          options: [true, false],
          data: false,
          col: 6,
        },
        {
          name: 'archive',
          label: 'Archive File',
          control: 'checkbox',
          hidden: !hasArchive,
          type: 'boolean',
          options: [true, false],
          data: false,
          col: 6,
        },
        {
          name: 'filename',
          label: 'Filename',
          col: 6,
          data: '{name}-{date}.{ext}',
        },
        {
          name: 'connectionIds',
          label: 'Upload',
          col: 6,
          control: 'tags',
          data: [],
          validation: {
            type: 'db',
            modelName: 'serverConnection',
          },
        },
        {
          name: 'tableName',
          label: 'Table Name',
          helpText: 'Use specific table name for BigQuery',
          col: 6,
          pattern: '^[A-Za-z_0-9]+$',
          maxLen: 50,
        },
        {
          type: 'section',
          style: 'blue',
          label: 'Run Options',
          fields: [
            {
              name: 'frequency',
              label: 'Frequency',
              required: true,
              col: 6,
              options: frequencyOptions,
            },
            {
              name: 'hour',
              label: 'Time of day',
              required: true,
              col: 6,
              options: hourOptions,
            },
            {
              name: 'startDate',
              label: 'Start Date',
              type: 'date',
              col: 6,
            },
            {
              name: 'endDate',
              label: 'End Date',
              type: 'date',
              col: 6,
            },
            {
              name: 'active',
              label: 'Active',
              control: 'checkbox',
              type: 'boolean',
              options: [true, false],
              data: true,
              col: 4,
            },
          ],
        },
      ],
    },
  ];

  const [layout, setLayout] = useState(getLayout());

  const getData = () => {
    if (!loading) {
      setLoading(true);
    }

    ApiService.get(`db/YMS11/${id}`).then(response => {
      const { data } = response;

      const branches = { data: [], dataAlt: [] };
      if (Array.isArray(data.branches)) {
        data.branches.forEach(branchId => {
          branches.data.push(branchId);
          for (const branch of user.branches) {
            if (branch.id === branchId) {
              branches.dataAlt.push(branch.name);
              return;
            }
          }
          branches.dataAlt.push('Unnamed');
        });
      }

      const connections = { data: [], dataAlt: [] };
      if (Array.isArray(data.connections)) {
        data.connections.forEach(connection => {
          connections.data.push(connection.id);
          connections.dataAlt.push(connection.name);
        });
      }

      data.connectionIds = connections;
      data.branches = branches;
      data.layoutId = String(data.layoutId);

      if (data.startDate) {
        data.startDate = moment(data.startDate).format('MM-DD-YYYY');
      }

      if (data.endDate) {
        data.endDate = moment(data.endDate).format('MM-DD-YYYY');
      }

      setLayout(
        new Layout(getLayout({ gridLayoutOptions: getGridLayoutOptions(data.pageName) }))
          .populateLayout(data)
          .getLayout()
      );

      setFetchingOptions(true);
      fetchOptions(data.pageName, data.params).then(({ layout, dynamic, options }) => {
        reportOptionsRef.current = options;
        setDynamic(dynamic);
        setReportLayout(layout);
        setLoading(false);
        setFetchingOptions(false);
      });
    });
  };

  const getLastValue = fieldName => {
    if (!formRef.current) {
      return null;
    }

    const manager = formRef.current.fec.current.manager;
    const history = manager.getHistory(fieldName);

    if (history.length <= 1) {
      return null;
    }

    const lastEntry = history[history.length - 2];
    return lastEntry ? lastEntry.data : manager.data[fieldName];
  };

  const getLayoutValue = fieldName => {
    return formRef.current && formRef.current.fec.current
      ? formRef.current.fec.current.manager.getValue(fieldName)
      : null;
  };

  const handleBlur = ({ name, value }) => {
    if (name === 'pageName') {
      if (value === getLastValue('pageName')) {
        return;
      }

      setFetchingOptions(true);
      fetchOptions(value).then(({ layout, dynamic, options }) => {
        reportOptionsRef.current = options;
        setDynamic(dynamic);
        setReportLayout(layout);
        setFetchingOptions(false);
      });
    }
  };

  const getCurrentPageName = () => {
    return getLayoutValue('pageName');
  };

  const handleReportOptionChange = () => {
    // keep track of report data state
    reportDataRef.current = getReportOptionsData();

    updateGridLayoutOptions(getCurrentPageName());
  };

  useEffect(() => {
    ApiService.get('grid-layouts', { params: { readable: true } }).then(response => {
      setGridLayouts(response.data || []);
    });
  }, []);

  useEffect(() => {
    if (gridLayouts && action === 'edit') {
      getData(id);
    }
  }, [gridLayouts]);

  useEffect(() => {
    setKey(key + 1);
  }, [reportLayout]);

  useEffect(() => {
    if (!fetchingOptions) {
      updateGridLayoutOptions(getCurrentPageName());
    }
  }, [fetchingOptions]);

  const submit = payload => {
    payload.layoutId = payload.layoutId || null;

    const reportHandler = reportFormRef.current.fec.current;
    reportHandler
      .submitForm()
      .then(() => {
        const reportData = reportHandler.manager.exportDataObject();
        const result = [];
        Object.keys(reportData).forEach(k => {
          if (k !== 'para') {
            result[k] = reportData[k];
          }
        });

        payload.params = result;

        if (['new', 'copy'].includes(action)) {
          ApiService.post('db/YMS11', payload).then(() => {
            toastr.success(`Created new job '${payload.name}'`);
            browserHistory.push('/YMS11');
          });
        } else {
          ApiService.put(`db/YMS11/${id}`, payload).then(() => {
            toastr.success(`Saved job '${payload.name}'`);
            browserHistory.push('/YMS11');
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  if (loading || !gridLayouts) {
    return <LoadingPage />;
  }

  return (
    <div className="scheduler rendered-form">
      <Col className="scheduler-column" sm={12} md={6} lg={6}>
        <Form
          key="main"
          ref={formRef}
          layout={layout}
          submit={submit}
          submitAll
          exportFormat="object"
          onBlur={handleBlur}
        />
      </Col>
      {reportLayout.length > 0 && (
        <Col className="scheduler-column report-options" sm={12} md={6} lg={6}>
          <Form
            key={key}
            ref={reportFormRef}
            layout={reportLayout}
            onChange={handleReportOptionChange}
            hideSubmit
            hideCancel
            exportFormat="object"
          />
        </Col>
      )}
    </div>
  );
};

Scheduler.propTypes = {
  id: PropTypes.string,
  action: PropTypes.string,
};

export default Scheduler;
